import { useMemo, useRef, useState } from "react";
import { isAxiosError } from "axios";
import { useAuth } from "@/hooks/use-auth";
import { usePermission } from "@/hooks/use-permission";
import { APP_CONSTANTS } from "@/config";
import { Button } from "@/components/ui/button";
import { FormDraftCardView } from "@/components/generative-ui/forms/FormDraftCard";
import type { FormDraftCardEnvelope, CardApprovalFailure } from "@/components/generative-ui/envelope/types";
import { submitCopilotForm, type FormReceipt, type FormRequest } from "@/services/copilot-forms";
import { FORM_PERMISSIONS, FormPreparationError, formCapability, ownerFormPayload, reviewCard } from "./copilot-form-payload";

type PendingSubmission = { request: FormRequest; values: Record<string, unknown>; receipt?: FormReceipt };

/** Session-scoped recovery retains the exact human DTO across drawer/reload.
 * Keys include both tenant and actor; nothing is logged or sent to the model. */
function restore(key: string): PendingSubmission | null {
  const raw = sessionStorage.getItem(key);
  if (!raw) return null;
  try { return JSON.parse(raw) as PendingSubmission; } catch { return null; }
}

type Props = { card: FormDraftCardEnvelope; data: Record<string, unknown>; previouslySubmitted?: boolean };
export function OwnerFormDraft(props: Props) {
  const { user } = useAuth();
  const tenant = localStorage.getItem(APP_CONSTANTS.COMPANY_LOCAL_STORAGE_KEY);
  return <OwnerFormDraftBody key={tenant + ":" + user?.id + ":" + props.card.card_id} {...props} />;
}

function OwnerFormDraftBody({ card, data, previouslySubmitted = false }: Props) {
  const { user } = useAuth();
  const { checkPermission } = usePermission();
  const tenant = localStorage.getItem(APP_CONSTANTS.COMPANY_LOCAL_STORAGE_KEY);
  const storageKey = `copilot-form:${tenant}:${user?.id}:${card.card_id}`;
  const [pending, setPending] = useState(() => restore(storageKey));
  // Only the request identity persists beyond this tab; patient values remain
  // session-scoped. A lost body must never become permission for a new write.
  const guardKey = `${storageKey}:submitted`;
  const [recoveryBlocked, setRecoveryBlocked] = useState(() => !pending && localStorage.getItem(guardKey) !== null);
  const [saving, setSaving] = useState(false);
  const submitting = useRef(false);
  const [cancelled, setCancelled] = useState(false);
  const [failure, setFailure] = useState<CardApprovalFailure>();
  const [notice, setNotice] = useState<string | null>(null);
  const capability = formCapability(card.action);
  const sameTenant = Boolean(tenant && card.tenant_id === tenant);
  const allowed = Boolean(sameTenant && capability && user && checkPermission(...FORM_PERMISSIONS[capability]) && (!previouslySubmitted || pending) && !recoveryBlocked);
  const form = useMemo(() => {
    const reviewed = reviewCard(card, data);
    return pending ? { ...reviewed, fields: reviewed.fields.map((field) => ({ ...field,
      value: pending.values[field.key] === null ? null : String(pending.values[field.key] ?? field.value ?? ""),
    })) } : reviewed;
  }, [card, data, pending]);

  async function submit(values?: Record<string, unknown>) {
    if (!capability || !allowed || submitting.current || pending?.receipt) return;
    let next: PendingSubmission;
    try {
      if (!pending && localStorage.getItem(guardKey) !== null) {
        setRecoveryBlocked(true);
        return;
      }
      next = pending ?? { request: { request_key: crypto.randomUUID(), payload: ownerFormPayload(capability, data, values ?? {}) }, values: values ?? {} };
      // Persist before HTTP. If recovery storage is unavailable, do not write.
      sessionStorage.setItem(storageKey, JSON.stringify(next));
      localStorage.setItem(guardKey, next.request.request_key);
    } catch (error) {
      setNotice(error instanceof FormPreparationError ? error.message : "Não foi possível preparar o envio. Seu rascunho foi mantido.");
      return;
    }
    submitting.current = true;
    setPending(next);
    setSaving(true);
    setFailure(undefined);
    setNotice(null);
    try {
      const receipt = await submitCopilotForm(capability, next.request);
      const confirmed = { ...next, receipt };
      sessionStorage.setItem(storageKey, JSON.stringify(confirmed));
      setPending(confirmed);
    } catch (error) {
      const status = isAxiosError(error) ? error.response?.status : undefined;
      const code = isAxiosError(error) ? error.response?.data?.error : undefined;
      const schedulingConflict = status === 409 && code === "effect_domain_conflict";
      if (status === 422 || schedulingConflict) {
        sessionStorage.removeItem(storageKey);
        localStorage.removeItem(guardKey);
        setPending(null);
        const fields: Record<string, string[]> = {};
        const raw = isAxiosError(error) ? error.response?.data?.errors : null;
        const fieldMap: Record<string, string> = { current_quantity: "quantity", min_quantity: "min_threshold", hour: "time", employeesId: "professional", proceduresId: "procedure", patientsId: "patient_name" };
        if (raw && typeof raw === "object") for (const key of Object.keys(raw)) {
          const ownerKey = key.replace(/^payload\./, "").split(".")[0];
          fields[fieldMap[ownerKey] ?? ownerKey] = ["Confira este campo antes de salvar."];
        }
        if (schedulingConflict) {
          fields.time = ["Escolha outro horário disponível."];
        }
        setFailure({ userMessage: schedulingConflict
          ? "O horário não está mais disponível. Revise a agenda e escolha outro horário."
          : code === "effect_domain_refused"
            ? "Um dos registros não está mais disponível. Revise os dados do formulário."
            : "Não foi possível salvar. Corrija os campos marcados.", fields });
      } else {
        setNotice(status === 403 ? "Esta ação não está autorizada agora. Peça ao administrador para verificar sua permissão e as regras da ação."
          : status === 409 && code === "effect_identity_conflict" ? "O envio conflita com uma confirmação anterior. Mantenha estes dados e peça ao suporte para verificar o resultado."
            : "Resultado não confirmado. Mantenha estes dados e verifique o mesmo envio antes de iniciar outra ação.");
      }
    } finally {
      submitting.current = false;
      setSaving(false);
    }
  }

  const unknown = pending !== null && !pending.receipt;
  return <div className="space-y-2" aria-busy={saving}>
    <p className="text-xs text-app-fg">Revise todos os campos. A gravação será autorizada pela clínica ao salvar.</p>
    {capability === "confirm_appointment" && <p className="text-xs text-app-fg">Sem retorno e sem observações. Salvar registra o agendamento; envio de mensagem e sincronização externa têm estados separados.</p>}
    {capability === "confirm_stock_item" && <p className="text-xs text-app-fg">Sem categoria e sem descrição adicional.</p>}
    {!sameTenant ? <p role="alert" className="text-sm text-app-fg">Este rascunho não pertence à clínica selecionada. Abra novamente o contexto na clínica correta.</p>
      : recoveryBlocked ? <p role="alert" className="text-sm text-app-fg">Esta revisão já foi enviada em outra sessão. Para evitar duplicação, a gravação está bloqueada. Confira o cadastro ou peça ao suporte para verificar o envio anterior.</p>
      : !allowed && <p role="status" className="text-sm text-app-fg">Você pode revisar o rascunho. Para salvar, é necessária permissão para esta ação.</p>}
    <FormDraftCardView card={form} disabled={saving || unknown || !allowed}
      state={pending?.receipt ? "approved" : cancelled ? "rejected" : failure ? "failed" : "pending"}
      approvedLabel="Salvo" failure={failure}
      onAction={(action) => {
        if (action.intent === "approve_form_draft") void submit(action.payload.values);
        if (action.intent === "cancel_form_draft") setCancelled(true);
      }} />
    {saving && <p role="status" className="text-sm text-app-fg">Aguardando confirmação de gravação…</p>}
    {pending?.receipt && <p role="status" className="text-sm text-app-fg">{pending.receipt.effect.replayed ? "Gravação anterior confirmada. Nenhuma duplicação." : "Gravação confirmada pela clínica."}</p>}
    {notice && <p role="alert" className="text-sm text-app-danger">{notice}</p>}
    {unknown && !saving && <><p className="text-sm text-app-fg">A edição fica bloqueada até confirmar o resultado deste envio.</p><Button variant="outline" disabled={!allowed} onClick={() => void submit()}>Verificar mesmo envio</Button></>}
  </div>;
}
